import classNames from 'classnames';
import type React from 'react';
import { useLocation } from 'react-router-dom';

import { RevelioTooltip, Skeleton } from '@revelio/ui';

import { useActivityLevelLabels } from '../../../../shared/selection-lists/hooks/useActivityLevelLabels';
import { useCustomSkillLevelLabels } from '../../../../shared/selection-lists/hooks/useCustomSkillLevelLabels';
import {
  ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS,
  SKILL_TAXONOMY_SELECTION_LIST_KEYS,
  type SkillTaxonomySelectionListKey,
} from '../../../../shared/selection-lists/types';
import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import {
  formatFilterCurrencyValue,
  isFundingFilterId,
  isRateFilterId,
} from '../../../../shared/user-preferences/utils/currency-formatting';
import previewStyles from '../../components/SavedViews/saved-view-preview.module.css';
import { shouldShowCurrentPreviousToggle } from '../config/currentPreviousConfig';
import { useFilterContext } from '../contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../hooks/useAvailableDateRangeWithTrialLag';
import { useViewDateGranularity } from '../hooks/useViewDateGranularity';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilterInstance,
} from '../types/filters';
import {
  isMinEffectivelySet as checkIsMinEffectivelySet,
  isMaxEffectivelySet,
  isYearFilter,
} from '../utils/common';
import { getFormattedDateString } from '../utils/dateUtils';
import {
  formatValueList,
  getFilterOperatorText,
} from '../utils/formatFilterValue';
import { selectionNameLooksUnresolved } from '../utils/patchUnresolvedSelectionNames';
import { ChipCloseButton } from './ChipCloseButton';
import { FilterPopover } from './FilterPopover';
import styles from './filter-chip.module.css';

export interface FilterTagProps {
  filter: Filter;
  onRemoveFilter: (filterId: FilterId) => void;
  disabled?: boolean;
  hideDisabledTooltip?: boolean;
  /** Draws the chip as a saved view's preview: preview style, nothing to click. */
  isPreview?: boolean;
}

const isSkillTaxonomyKey = (k: string): k is SkillTaxonomySelectionListKey =>
  (SKILL_TAXONOMY_SELECTION_LIST_KEYS as readonly string[]).includes(k);

const getHumanReadableLevelId = (levelId: string): string | null => {
  if (levelId === 'role_k7') {
    return 'Job Category';
  }

  if (levelId.includes('_k')) {
    return levelId.split('_')[1] ?? null;
  }

  if (levelId === 'msa') {
    return 'Metro Area';
  }

  if (levelId === 'company') {
    return 'company';
  }

  if (isSkillTaxonomyKey(levelId)) {
    return null;
  }

  // Activity levels resolve through useActivityLevelLabels; never fall through
  // to the raw-key split, which would render the internal `k150` token.
  if (
    (ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS as readonly string[]).includes(
      levelId
    )
  ) {
    return null;
  }

  return levelId.charAt(0).toUpperCase() + levelId.slice(1);
};

export const FilterTag: React.FC<FilterTagProps> = ({
  filter,
  onRemoveFilter,
  disabled = false,
  hideDisabledTooltip = false,
  isPreview = false,
}) => {
  const viewGranularity = useViewDateGranularity();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { labelsByDepth } = useCustomSkillLevelLabels(0);
  const { dispatch, isResolvingSelectionLabels, activeFilterMap } =
    useFilterContext();
  // Level labels must come from the taxonomy the selections belong to (the
  // resolved adaptive/preference id in filter state), mirroring the picker.
  const activityTaxonomyFilter = activeFilterMap.get(
    FilterId.ACTIVITY_TAXONOMY
  );
  const activityTaxonomyValue =
    activityTaxonomyFilter?.type === FilterType.VALUE
      ? (activityTaxonomyFilter.value as {
          taxonomyId?: number;
          version?: string;
        })
      : undefined;
  const { labelsByDepth: activityLabelsByDepth } = useActivityLevelLabels({
    enabled: filter.id === FilterId.ACTIVITY,
    taxonomyId: activityTaxonomyValue?.taxonomyId ?? 0,
    taxonomyVersion: activityTaxonomyValue?.version,
  });
  const location = useLocation();
  const { currencySymbol, exchangeRate } = useCurrencyPreference();

  // Name-shaped placeholder shown while a tree/list filter's selection labels are
  // still resolving — its name is currently a raw id awaiting the reference tree.
  // Without this the chip flashes the numeric id (e.g. "49") before the taxonomy
  // tree lands and the label patcher fills in the real name (e.g. "Data Scientist").
  const nameSkeleton = <Skeleton className={styles.nameSkeleton} />;

  const filterChipName = (() => {
    const shouldShowCurrentPrevious = shouldShowCurrentPreviousToggle(
      filter.id,
      location.pathname
    );

    const name = filter.name;

    if (!shouldShowCurrentPrevious || !('isCurrent' in filter)) {
      return name;
    }

    const prefix = filter.isCurrent ? 'Current' : 'Previous';
    return `${prefix} ${name}`;
  })();

  const handleRemoveFilter = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMultiFilterInstance(filter)) {
      dispatch({
        type: 'REMOVE_MULTI_FILTER_INSTANCE',
        payload: {
          parentId: filter.parentMultiFilterId,
          instanceIndex: filter.instanceIndex,
        },
      });
      return;
    }
    onRemoveFilter(filter.id);
  };

  const renderDateFilterTag = () => {
    if (
      !availableDateRange ||
      (filter.type !== FilterType.DATE_RANGE && filter.type !== FilterType.DATE)
    ) {
      return null;
    }

    if (!('min' in filter) || !('max' in filter)) {
      return null;
    }

    const startDate = filter.min;
    const endDate = filter.max;

    if (!startDate || !endDate) {
      return null;
    }

    const displayStartDate = getFormattedDateString(startDate, viewGranularity);
    const displayEndDate = getFormattedDateString(endDate, viewGranularity);

    if (filter.id === FilterId.SNAPSHOT_DATE) {
      return <span className={styles.value}>{displayStartDate}</span>;
    }

    return (
      <span className={styles.inlineRow}>
        <span className={styles.value}>{displayStartDate}</span>
        <span>to</span>
        <span className={styles.value}>{displayEndDate}</span>
      </span>
    );
  };

  const filterValue = (() => {
    switch (filter.type) {
      case FilterType.DATE_RANGE:
      case FilterType.DATE:
        return renderDateFilterTag();
      case FilterType.RANGE:
        if ('min' in filter && 'max' in filter) {
          const min = filter.min;
          const max = filter.max;
          const isPayFilter = filter.id === FilterId.PAY;
          const filterIsFunding = isFundingFilterId(filter.id);
          const isRateFilter = isRateFilterId(filter.id);
          const formatValue = (value: number) => {
            return formatFilterCurrencyValue({
              value,
              exchangeRate,
              currencySymbol,
              filterId: filter.id,
              isPayFilter,
              isFundingFilter: filterIsFunding,
              isRateFilter,
            });
          };

          const isMinEffectivelySet = checkIsMinEffectivelySet(
            min,
            filter.id,
            isPayFilter,
            filterIsFunding
          );

          const maxEffectivelySet = isMaxEffectivelySet(
            max,
            filter.id,
            isMinEffectivelySet
          );

          if (isMinEffectivelySet && !maxEffectivelySet) {
            if (min === undefined) return null;
            return (
              <span className={styles.inlineRow}>
                <span>greater than</span>
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </span>
              </span>
            );
          }

          if (!isMinEffectivelySet && maxEffectivelySet) {
            if (max === undefined) return null;
            return (
              <span className={styles.inlineRow}>
                <span>less than</span>
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </span>
              </span>
            );
          }

          if (isMinEffectivelySet && maxEffectivelySet) {
            if (max === undefined || min === undefined) return null;
            if (min === max) {
              return (
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </span>
              );
            }

            return (
              <span className={styles.inlineRow}>
                {!isYearFilter(filter.id) && <span>from</span>}
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </span>
                <span>to</span>
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </span>
              </span>
            );
          }

          if (!isMinEffectivelySet && !maxEffectivelySet) {
            if (min === undefined || max === undefined) return null;
            return (
              <span className={styles.inlineRow}>
                <span>less than</span>
                <span className={styles.value}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </span>
              </span>
            );
          }
        }
        return null;

      case FilterType.STATIC_LIST:
      case FilterType.LIST:
        if ('selections' in filter) {
          const displayName = formatValueList(
            filter.selections.map((s) => s.name)
          );

          const hasUnresolvedName = filter.selections.some((s) =>
            selectionNameLooksUnresolved(s.id, s.name)
          );
          if (hasUnresolvedName && isResolvingSelectionLabels) {
            return <span className={styles.value}>{nameSkeleton}</span>;
          }

          if (displayName) {
            return <span className={styles.value}>{displayName}</span>;
          }
        }
        return null;

      case FilterType.TREE:
        if ('selections' in filter) {
          const displayName = formatValueList(
            filter.selections.map((s) => s.name)
          );

          const displayLevelId = (() => {
            if (filter.id === FilterId.CUSTOM_SKILL) {
              const labels = Array.from(
                new Set(
                  filter.selections
                    .map((s) => {
                      const match = String(s.levelId).match(/^skill_(\d+)/);
                      const depth = match ? Number(match[1]) : null;
                      return depth !== null && depth !== undefined
                        ? (labelsByDepth?.[depth] ?? null)
                        : null;
                    })
                    .filter((v): v is string => Boolean(v))
                )
              );
              return labels.join('/');
            }

            if (filter.id === FilterId.ACTIVITY) {
              const labels = Array.from(
                new Set(
                  filter.selections
                    .map((s) => {
                      const depth = (
                        ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS as readonly string[]
                      ).indexOf(String(s.levelId));
                      return depth >= 0
                        ? (activityLabelsByDepth?.[depth] ?? null)
                        : null;
                    })
                    .filter((v): v is string => Boolean(v))
                )
              );
              return labels.join('/');
            }

            const levelIds = Array.from(
              new Set(
                filter.selections
                  .map((s) => getHumanReadableLevelId(s.levelId))
                  .filter((v): v is string => v !== null)
              )
            );

            // For COMPANY/COMPETITORS filter: only show "company" if there are also industry selections
            if (
              filter.id === FilterId.COMPANY ||
              filter.id === FilterId.COMPETITORS ||
              filter.id === FilterId.FOUNDER_COMPANY
            ) {
              const hasIndustrySelections = filter.selections.some(
                (s) =>
                  s.levelId === 'rics_k10' ||
                  s.levelId === 'rics_k50' ||
                  s.levelId === 'rics_k400'
              );
              if (!hasIndustrySelections) {
                // Filter out 'company' from display if only companies are selected
                return levelIds.filter((id) => id !== 'company').join('/');
              }
            }

            return levelIds.join('/');
          })();

          // Labels still loading: show a placeholder (keeping the known level)
          // instead of the raw id. Gated on the fetching flag so a genuinely
          // unresolvable id falls back to its raw value once loading completes
          // rather than shimmering forever.
          const hasUnresolvedName = filter.selections.some((s) =>
            selectionNameLooksUnresolved(s.id, s.name)
          );
          if (hasUnresolvedName && isResolvingSelectionLabels) {
            return (
              <span className={styles.value}>
                {nameSkeleton}
                {displayLevelId ? ` (${displayLevelId})` : ''}
              </span>
            );
          }

          if (displayName) {
            return (
              <span className={styles.value}>
                {displayName}
                {displayLevelId ? ` (${displayLevelId})` : ''}
              </span>
            );
          }
        }
        return null;

      case FilterType.TEXT:
        if ('value' in filter) {
          return <span className={styles.value}>{filter.value}</span>;
        }
        return null;

      case FilterType.FREE_TEXT:
        if ('text' in filter) {
          const displayString = formatValueList(filter.text);

          if (displayString) {
            return <span className={styles.value}>{displayString}</span>;
          }
        }
        return null;

      case FilterType.SCHOOL:
        if ('schoolResultItems' in filter) {
          const displayName = formatValueList(
            Object.values(filter.schoolResultItems).map((school) => school.name)
          );
          return <span className={styles.value}>{displayName}</span>;
        }
        return null;

      default:
        return null;
    }
  })();

  const middleText = getFilterOperatorText(filter);

  const renderTag = ({ isDisabled }: { isDisabled: boolean }) => {
    return (
      <span
        className={classNames(
          styles.chip,
          styles.bgRaised,
          isDisabled && styles.disabled,
          isPreview && previewStyles.previewChip
        )}
        data-testid={
          isPreview
            ? 'saved-view-preview-filter-chip'
            : `filter-chip-${filter.id}`
        }
      >
        <span className={styles.chipRow}>
          <span className={styles.chipLabel}>
            <span className={styles.row}>
              <span className={styles.title}>{filterChipName}</span>
              <span>•</span>
              {middleText.trim() && (
                <>
                  <span>{middleText}</span>
                  <span>•</span>
                </>
              )}
              {filterValue}
            </span>
          </span>
          {!isPreview && <ChipCloseButton onClick={handleRemoveFilter} />}
        </span>
      </span>
    );
  };

  const renderTrigger = (() => {
    if (disabled) {
      return (
        <RevelioTooltip
          label="This filter is unavailable on this page."
          isDisabled={hideDisabledTooltip}
        >
          {renderTag({ isDisabled: true })}
        </RevelioTooltip>
      );
    }

    return renderTag({ isDisabled: false });
  })();

  if (isPreview) {
    return filterValue !== null && renderTag({ isDisabled: false });
  }

  return (
    filterValue !== null && (
      <FilterPopover
        filter={filter}
        trigger={<span className={styles.chipTrigger}>{renderTrigger}</span>}
      />
    )
  );
};
